import { fetchArray } from "entity";
import Inscricao from "entity/Inscricao";
import firebaseUtil from "firebaseUtil";
import { writeBatch } from "firebase/firestore";

/**
 * Copia os inscritos de uma rodada de origem para uma rodada de destino.
 *
 * - Ignora duplicados silenciosamente (mesmo CNPJ/CPF já inscrito na rodada destino)
 * - Copia todos os inscritos independente do status
 * - Copia todos os produtos (produtosCompra/produtosVenda) mesmo que não existam na rodada destino
 * - Reseta campos de controle (status, mesa, datas, questionário)
 *
 * @param {string} rodadaOrigemUid - UID da rodada de origem
 * @param {{ uid: string, nome: string }} rodadaDestino - Objeto com uid e nome da rodada de destino
 * @returns {Promise<{ copiados: number, ignorados: number }>} Resultado da operação
 */
export async function copiarInscritosDeRodada(rodadaOrigemUid, rodadaDestino) {
    // 1. Buscar inscritos da rodada de origem
    const queryOrigem = Inscricao.collection.where("rodada.uid", "==", rodadaOrigemUid);
    const inscritosOrigem = await fetchArray(queryOrigem.get());

    if (!inscritosOrigem.length) {
        return { copiados: 0, ignorados: 0, semInscritos: true };
    }

    // 2. Buscar inscritos já existentes na rodada destino para verificar duplicados
    const queryDestino = Inscricao.collection.where("rodada.uid", "==", rodadaDestino.uid);
    const inscritosDestino = await fetchArray(queryDestino.get());

    // Criar set de CNPJs/CPFs já inscritos na rodada destino
    const cnpjsExistentes = new Set();
    inscritosDestino.forEach((insc) => {
        const cnpj = insc.empresa?.cnpj;
        if (cnpj) {
            cnpjsExistentes.add(cnpj.replace(/[^\d]+/g, ""));
        }
    });

    // 3. Filtrar inscritos a copiar (ignorar duplicados)
    const inscritosParaCopiar = inscritosOrigem.filter((insc) => {
        const cnpj = insc.empresa?.cnpj;
        if (!cnpj) return true; // sem CNPJ, copia mesmo assim
        const cnpjLimpo = cnpj.replace(/[^\d]+/g, "");
        return !cnpjsExistentes.has(cnpjLimpo);
    });

    const ignorados = inscritosOrigem.length - inscritosParaCopiar.length;

    if (!inscritosParaCopiar.length) {
        return { copiados: 0, ignorados, semInscritos: false };
    }

    // 4. Copiar em lotes (batch write, máximo 500 por batch no Firestore)
    const BATCH_SIZE = 500;
    let copiados = 0;

    for (let i = 0; i < inscritosParaCopiar.length; i += BATCH_SIZE) {
        const lote = inscritosParaCopiar.slice(i, i + BATCH_SIZE);
        // const db = firebaseUtil.db;
        // const batch = writeBatch(db);

        lote.forEach((inscritoOriginal) => {
            const novoInscrito = prepararInscritoParaCopia(inscritoOriginal, rodadaDestino);
            // console.log('novoInscrito',novoInscrito)
            const entity = new Inscricao();
            entity.update(novoInscrito);
            entity
                .save()
                .then(() => {
                    console.log("Salvou a inscrição");
                })
                .catch((error) => {
                    console.error("Erro ao salvar inscrição:", error);
                    console.log('novoInscrito',novoInscrito)
                })
                .finally(() => {
                    console.log("Finalizando operação");
                });
            // const ref = entity.doc; // gera novo UID automaticamente

            // batch.set(ref, { ...entity.data }, { merge: true });
        });

        // await batch.commit();
        copiados += lote.length;
    }

    return { copiados, ignorados, semInscritos: false };
}

/**
 * Prepara um objeto de inscrição para ser copiado para uma nova rodada.
 * Remove/reseta campos de controle e ajusta a referência da rodada.
 */
function prepararInscritoParaCopia(inscritoOriginal, rodadaDestino) {
    // Clonar profundamente para não alterar o original
    const novo = JSON.parse(JSON.stringify(inscritoOriginal));

    // Remover UID (será gerado automaticamente)
    delete novo.uid;

    // Atualizar referência da rodada
    novo.rodada = {
        uid: rodadaDestino.uid,
        nome: rodadaDestino.nome,
    };

    // Resetar status
    novo.status = "INSCRITO";
    novo.statusEvento = "INSCRITO";

    // Limpar mesa (será atribuída na nova rodada)
    // novo.mesa = {};

    // Setar data de criação para agora (formato pt-BR usado pelo painel)
    const dataAtual = new Date().toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
    novo.dataFirstChange = dataAtual;

    // Limpar datas de controle de alteração
    delete novo.dataLastChange;
    delete novo.dataLastChangeStr;
    delete novo.dataLastChangeServer;
    delete novo.userLastChange;
    // if (novo.produtosCompra &&novo.produtosCompra.length > 200) {
    //     console.warn(`Inscrição ${inscritoOriginal.uid} possui mais de 200 produtosCompra.`);
    //     novo.produtosCompra = novo.produtosCompra.slice(0, 200);
    // }
    //  if (novo.produtosVenda && novo.produtosVenda.length > 200) {
    //     console.warn(`Inscrição ${inscritoOriginal.uid} possui mais de 200 produtosVenda.`);
    //     novo.produtosVenda = novo.produtosVenda.slice(0, 200);
    // }
    // Limpar respostas do questionário (questões podem diferir entre rodadas)
    // delete novo.respostaQuestionario;

    return novo;
}
